import React from 'react'
import { useTranslation } from 'react-i18next'
import { localizeGeoPortalUrl } from '@popety-io/utils'
import { Icon } from '../../atoms'
import {
  MetricSourceBadge,
  useMetricSourceBadgeLabel,
} from '../../atoms/MetricSourceBadge'
import { PDFViewer } from '../../organisms/PDFViewer'
import {
  ZoningReportData,
  zoning_indice_sources,
  zoning_sources_agreement,
  ZoningReference,
  ZoningMetricSources,
  ZoningEstimateStats,
} from '@popety-io/types'
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  HeaderTopRow,
  HeaderContext,
  ContextChevron,
  HeaderDivider,
  ModalTitle,
  CloseButton,
  DetailsPanel,
  PDFViewerPanel,
  Section,
  SectionTitle,
  ReferenceList,
  ReferenceItem,
  DocumentLink,
  AdditionalInfo,
  ValueHero,
  HeroLabel,
  HeroValue,
  HeroUnit,
  StatusBar,
  TabBar,
  Tab,
  TabValue,
  WarningBox,
  WarningContent,
  ReportLink,
  GeoportalButton,
  OverrideDescription,
  EmptyRightPanel,
  EmptyIcon,
  EmptyText,
  ManualTimelinePanel,
  TimelineTitle,
  TimelineStep,
  TimelineStepIndicator,
  TimelineStepCircle,
  TimelineConnector,
  TimelineStepContent,
  TimelineStepTitle,
  TimelineStepDescription,
  TimelineHighlightBox,
  TimelineHighlightTitle,
  TimelineHighlightDescription,
  GeoportalPanel,
  ReportFormContainer,
  ReportContextBlock,
  ReportContextRow,
  ReportContextLabel,
  ReportContextValue,
  ReportInput,
  ReportTextarea,
  ReportFieldLabel,
  ReportButtonGroup,
  ReportSubmitButton,
  ReportCancelButton,
  ReportSuccessMessage,
  ReportBoundSelector,
  ReportBoundOption,
} from './MetricSourceModal.styles'
import {
  getAvailableSources,
  shouldShowRightPanel,
  getRightPanelContent,
  getOriginalSource,
  getSourceValue,
  isAuthoritative,
  parseMetricKey,
} from './MetricSourceModal.helpers'
import { ZoningMapViewer } from '../../organisms/ZoningSection/components/ZoningMapViewer'
import { EstimatePanel } from './EstimatePanel'
import { MinMaxValue } from '../../organisms/ZoningSection/components/MinMaxLabel'
import { EnvelopeCalcRow } from '../../organisms/ZoningSection/components/utils/envelopeCalculation'
import {
  CalcList,
  CalcRow,
  CalcOp,
  CalcLabelWrap,
  CalcLabel,
  CalcSub,
  CalcValue,
  ThreeDChip,
  ReferenceCard,
  ReferenceCardTitle,
} from './MetricSourceModal.styles'

/* ─── Props ────────────────────────────────────────────────────── */

export interface MetricSourceModalProps {
  isOpen: boolean
  onClose: () => void
  metricLabel: string
  unit?: string
  // v2.0 multi-source data
  value?: number | string
  /** Whether this metric has both min and max values */
  isRange?: boolean
  /** Separate min value for labeled display */
  minValue?: number
  /** Separate max value for labeled display */
  maxValue?: number
  defaultSource?: zoning_indice_sources
  sourcesAgreement?: zoning_sources_agreement
  sources?: ZoningMetricSources
  // v2.0 per-bound data (used when isRange)
  minSources?: ZoningMetricSources
  maxSources?: ZoningMetricSources
  minDefaultSource?: zoning_indice_sources
  maxDefaultSource?: zoning_indice_sources
  minSourcesAgreement?: zoning_sources_agreement
  maxSourcesAgreement?: zoning_sources_agreement
  geoPortalLink?: string
  centerChProjection?: string
  geoCenter?: { lat: number; lon: number } | [number, number] | string
  geoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  landGeoPolygon?: { type: string; coordinates: number[][][] | number[][][][] }
  regionName?: string
  // Zone context for reporting
  landId?: string
  zoneId?: string
  zoneName?: string
  planName?: string
  zoneType?: string
  municipalityName?: string
  // Estimate stats for ALG justification panel
  estimateStats?: ZoningEstimateStats | null
  estimateStatsLoading?: boolean
  metricKey?: string
  // Report callback
  onReportIncorrect?: (data: ZoningReportData) => Promise<void>
  /**
   * Regelbauweise envelope: renders a calculation breakdown between the hero
   * and the source tabs. All copy is provided by the caller (no i18n keys
   * inside the modal for these).
   */
  envelopeCalculation?: {
    calculatedValueLabel?: string
    title?: string
    rows: EnvelopeCalcRow[]
    disclaimer?: string
  }
  // Legacy props for backward compatibility during migration
  /** @deprecated Use sources instead */
  source?: zoning_indice_sources
  /** @deprecated Use sources instead */
  reference?: ZoningReference
  /** @deprecated Use sources instead */
  additionalInfo?: string
}

/* ─── Component ────────────────────────────────────────────────── */

export const MetricSourceModal: React.FC<MetricSourceModalProps> = ({
  isOpen,
  onClose,
  metricLabel,
  value,
  isRange,
  minValue,
  maxValue,
  defaultSource,
  sourcesAgreement,
  sources,
  minSources,
  maxSources,
  minDefaultSource,
  maxDefaultSource,
  minSourcesAgreement,
  maxSourcesAgreement,
  geoPortalLink,
  geoCenter,
  geoPolygon,
  landGeoPolygon,
  regionName,
  landId,
  zoneId,
  zoneName,
  planName,
  zoneType,
  municipalityName,
  estimateStats,
  estimateStatsLoading,
  metricKey,
  onReportIncorrect,
  unit = '',
  envelopeCalculation,
  source,
  reference,
  additionalInfo,
}) => {
  const { t, i18n } = useTranslation('ui')
  const getBadgeLabel = useMetricSourceBadgeLabel()
  const localizedGeoPortalLink = geoPortalLink
    ? localizeGeoPortalUrl(
        geoPortalLink,
        i18n.resolvedLanguage || i18n.language
      )
    : undefined

  // Active tab state — reset when modal opens
  const [activeTab, setActiveTab] =
    React.useState<zoning_indice_sources | null>(null)

  const [selectedBound, setSelectedBound] = React.useState<'min' | 'max'>('max')

  // Report form state
  const [reportMode, setReportMode] = React.useState(false)
  const [reportBound, setReportBound] = React.useState<'min' | 'max'>('max')
  const [reportSuggestedValue, setReportSuggestedValue] = React.useState('')
  const [reportExplanation, setReportExplanation] = React.useState('')
  const [reportSubmitting, setReportSubmitting] = React.useState(false)
  const [reportSuccess, setReportSuccess] = React.useState(false)
  const [reportError, setReportError] = React.useState(false)

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab(null)
      setReportMode(false)
      setReportBound('max')
      setReportSuggestedValue('')
      setReportExplanation('')
      setReportSubmitting(false)
      setReportSuccess(false)
      setReportError(false)
      setSelectedBound('max')
    }
  }, [isOpen])

  if (!isOpen) return null

  /* ── Bound-aware active data ──────────────────────────────────── */
  const isRangeV2 =
    !!isRange && (minSources !== undefined || maxSources !== undefined)

  // When MIN and MAX point to the same references (ignoring the bound-specific
  // numeric `value`), there is nothing to toggle — hide the bound selector.
  const stripSourceValues = (src?: ZoningMetricSources): string => {
    if (!src) return ''
    const cleaned: Record<string, unknown> = {}
    for (const [key, entry] of Object.entries(src)) {
      if (!entry) continue
      const { value: _v, ...rest } = entry as Record<string, unknown>
      cleaned[key] = rest
    }
    return JSON.stringify(cleaned)
  }
  const boundsIdentical =
    isRangeV2 &&
    minDefaultSource === maxDefaultSource &&
    minSourcesAgreement === maxSourcesAgreement &&
    stripSourceValues(minSources) === stripSourceValues(maxSources)

  const boundSources = isRangeV2
    ? selectedBound === 'min'
      ? minSources
      : maxSources
    : undefined
  const boundDefaultSource = isRangeV2
    ? selectedBound === 'min'
      ? minDefaultSource
      : maxDefaultSource
    : undefined
  const boundAgreement = isRangeV2
    ? selectedBound === 'min'
      ? minSourcesAgreement
      : maxSourcesAgreement
    : undefined
  const boundValue = isRangeV2
    ? selectedBound === 'min'
      ? minValue
      : maxValue
    : undefined

  /* ── Legacy fallback (single-value metrics) ──────────────────── */
  const fallbackSources: ZoningMetricSources | undefined =
    sources ||
    (source
      ? ({
          [source]: {
            value: typeof value === 'number' ? value : Number(value),
            ...(source === 'llm' && reference ? { reference } : {}),
            ...(additionalInfo ? { additional_info: additionalInfo } : {}),
          },
        } as ZoningMetricSources)
      : undefined)

  const effectiveSources: ZoningMetricSources | undefined = isRangeV2
    ? boundSources
    : fallbackSources
  const effectiveDefaultSource = isRangeV2
    ? boundDefaultSource
    : defaultSource || source
  const effectiveAgreement = isRangeV2
    ? boundAgreement || 'unique'
    : sourcesAgreement || 'unique'
  const effectiveValue = isRangeV2 ? boundValue : value

  /* ── Derived state ────────────────────────────────────────────── */
  const available = getAvailableSources(effectiveSources)
  const isManualOverride = effectiveDefaultSource === 'manual'
  const isConflict = effectiveAgreement === 'conflict'
  const isIdentical = effectiveAgreement === 'identical'
  const isNoData = available.length === 0 && effectiveValue === undefined

  // Set initial active tab when sources change
  const initialTab = isManualOverride
    ? 'manual'
    : effectiveDefaultSource || available[0] || null
  const currentTab =
    activeTab && available.includes(activeTab) ? activeTab : initialTab

  const hasRightPanel = shouldShowRightPanel(currentTab, effectiveSources)
  const rightPanelContent = getRightPanelContent(currentTab, effectiveSources)
  const llmRef = effectiveSources?.llm?.reference?.[0]
  const pdfUrl = llmRef?.document_url
  const pdfInitialPage =
    currentTab === 'llm' && llmRef?.page_start ? llmRef.page_start - 1 : 0

  // Track which reference is being shown as inline PDF (to hide "View PDF" link for it)
  const inlinePdfUrl = rightPanelContent === 'pdf' ? pdfUrl : undefined

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  /* ── Report form ─────────────────────────────────────────────── */
  const isNAValue = value === 'N/A'
  const isRangeReport =
    isRange && minValue !== undefined && maxValue !== undefined
  const displayedValueNum =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number(value)
        : undefined

  const canReport =
    onReportIncorrect &&
    (isNAValue ||
      isRangeReport ||
      (displayedValueNum !== undefined && !isNaN(displayedValueNum)))

  const handleReportSubmit = async () => {
    if (!canReport || !metricKey || !zoneType || !zoneName || reportSubmitting)
      return
    setReportSubmitting(true)
    setReportError(false)
    try {
      const { metricColumn } = parseMetricKey(metricKey)
      const reportValue = isRangeReport
        ? reportBound === 'min'
          ? minValue!
          : maxValue!
        : isNAValue
          ? null
          : displayedValueNum!
      const reportMetricBound = isRangeReport
        ? reportBound
        : parseMetricKey(metricKey).metricBound
      await onReportIncorrect({
        landId: landId || '',
        zoneId: zoneId || '',
        zoneType: zoneType as any,
        zoneName,
        regionName: regionName || '',
        municipalityName: municipalityName || '',
        metricColumn,
        metricBound: reportMetricBound,
        displayedName: metricLabel,
        displayedValue: reportValue,
        source: currentTab || effectiveDefaultSource || 'unknown',
        suggestedValue: reportSuggestedValue
          ? Number(reportSuggestedValue)
          : undefined,
        explanation: reportExplanation,
      })
      setReportSuccess(true)
      setTimeout(() => {
        setReportMode(false)
        setReportSuccess(false)
        setReportSuggestedValue('')
        setReportExplanation('')
      }, 2000)
    } catch {
      setReportError(true)
    } finally {
      setReportSubmitting(false)
    }
  }

  const handleReportCancel = () => {
    setReportMode(false)
    setReportBound('max')
    setReportSuggestedValue('')
    setReportExplanation('')
  }

  /* ── Bound selector (range metrics only) ─────────────────────── */
  const renderBoundSelector = () => {
    if (!isRangeV2 || boundsIdentical) return null
    return (
      <ReportBoundSelector
        style={{ marginTop: 12, marginBottom: 4 }}
        role="group"
        aria-label={t('metricSourceModal.boundSelector.ariaLabel', {
          defaultValue: 'Select min or max',
        })}
      >
        <ReportBoundOption
          $selected={selectedBound === 'min'}
          onClick={() => setSelectedBound('min')}
          role="radio"
          aria-checked={selectedBound === 'min'}
        >
          {t('metricSourceModal.boundSelector.min', { defaultValue: 'MIN' })}
          {minValue !== undefined ? `: ${minValue}` : ''}
          {unit ? ` ${unit}` : ''}
        </ReportBoundOption>
        <ReportBoundOption
          $selected={selectedBound === 'max'}
          onClick={() => setSelectedBound('max')}
          role="radio"
          aria-checked={selectedBound === 'max'}
        >
          {t('metricSourceModal.boundSelector.max', { defaultValue: 'MAX' })}
          {maxValue !== undefined ? `: ${maxValue}` : ''}
          {unit ? ` ${unit}` : ''}
        </ReportBoundOption>
      </ReportBoundSelector>
    )
  }

  /* ── Hero section ─────────────────────────────────────────────── */
  const renderHero = () => {
    let label: string
    let badges: zoning_indice_sources[] = []

    if (envelopeCalculation?.calculatedValueLabel) {
      // Regelbauweise envelope: the hero is a derived value, not a raw source
      label = envelopeCalculation.calculatedValueLabel
      if (effectiveDefaultSource) badges = [effectiveDefaultSource]
    } else if (isNoData) {
      label = t('metricSourceModal.labels.value')
    } else if (isManualOverride) {
      label = t('metricSourceModal.labels.userValue')
      badges = ['manual']
    } else if (isConflict) {
      label = t('metricSourceModal.labels.displayedValue')
      if (effectiveDefaultSource) badges = [effectiveDefaultSource]
    } else if (isIdentical) {
      label = t('metricSourceModal.labels.confirmedValue')
      badges = available.filter((s) => s !== 'manual')
    } else {
      // unique
      label = t('metricSourceModal.labels.value')
      if (effectiveDefaultSource) badges = [effectiveDefaultSource]
    }

    const isTextValue =
      !isNoData &&
      !isRange &&
      typeof value === 'string' &&
      value !== 'N/A' &&
      !Number.isFinite(Number(value))

    // Swiss thousand separator for surface/volume values in the hero —
    // same rule as formatMetricValue in the zoning table.
    const swissFormat = (
      v: number | string | undefined
    ): number | string | undefined =>
      typeof v === 'number' && (unit === 'm²' || unit === 'm³')
        ? Math.round(v)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, "'")
        : v

    return (
      <ValueHero>
        <HeroLabel>
          {label}
          {badges.map((src) => (
            <MetricSourceBadge key={src} source={src} />
          ))}
        </HeroLabel>
        <HeroValue $isOverride={isManualOverride} $compact={isTextValue}>
          {isNoData ? (
            <span style={{ color: '#ccc' }}>&mdash;</span>
          ) : isRange && minValue !== undefined && maxValue !== undefined ? (
            <>
              <MinMaxValue
                minDisplay={String(swissFormat(minValue))}
                maxDisplay={String(swissFormat(maxValue))}
              />
              {unit && <HeroUnit>{unit}</HeroUnit>}
            </>
          ) : (
            <>
              {maxValue !== undefined ? (
                <MinMaxValue maxDisplay={String(swissFormat(value))} />
              ) : minValue !== undefined ? (
                <MinMaxValue minDisplay={String(swissFormat(value))} />
              ) : (
                swissFormat(value)
              )}
              {unit && <HeroUnit>{unit}</HeroUnit>}
            </>
          )}
        </HeroValue>
      </ValueHero>
    )
  }

  /* ── Status bar ───────────────────────────────────────────────── */
  const renderStatusBar = () => {
    if (isNoData) return null

    // Override bar takes precedence over conflict bar
    if (isManualOverride) {
      const originalSrc = getOriginalSource(effectiveSources)
      const originalVal = originalSrc
        ? getSourceValue(effectiveSources, originalSrc)
        : undefined

      return (
        <StatusBar>
          <Icon name="alert-circle" size="small" />
          {t('metricSourceModal.agreement.userModified', {
            source: originalSrc ? getBadgeLabel(originalSrc) : '?',
            value:
              originalVal !== undefined
                ? `${originalVal}${unit ? ` ${unit}` : ''}`
                : '—',
          })}
        </StatusBar>
      )
    }

    if (isConflict) {
      return (
        <WarningBox $type="conflict">
          <WarningContent>
            <Icon name="alert-circle" size="medium" />
            <div>{t('metricSourceModal.agreement.conflict')}</div>
          </WarningContent>
        </WarningBox>
      )
    }

    // No bar for single source or identical agreement
    return null
  }

  /* ── Tabs ─────────────────────────────────────────────────────── */
  const renderTabs = () => {
    // No tabs for single source or no data
    if (available.length < 2 && !isManualOverride) return null
    // If manual override, show manual + available non-manual sources
    const tabSources = isManualOverride
      ? [
          'manual' as zoning_indice_sources,
          ...available.filter((s) => s !== 'manual'),
        ]
      : available

    if (tabSources.length < 2) return null

    return (
      <TabBar>
        {tabSources.map((src) => {
          const srcValue = getSourceValue(effectiveSources, src)
          const isActive = currentTab === src
          const isLosingSource =
            isConflict && src !== effectiveDefaultSource && src !== 'manual'
          const isOverridden = isManualOverride && src !== 'manual'

          return (
            <Tab key={src} $active={isActive} onClick={() => setActiveTab(src)}>
              <MetricSourceBadge source={src} faded={isOverridden} />
              <TabValue
                $struck={isLosingSource || isOverridden}
                $color={
                  isLosingSource ? '#cc3300' : isOverridden ? '#999' : undefined
                }
              >
                {srcValue !== undefined ? srcValue : '—'}
              </TabValue>
            </Tab>
          )
        })}
      </TabBar>
    )
  }

  /* ── Reference info (for REG tab) ─────────────────────────────── */
  const renderSingleReference = (ref: ZoningReference) => {
    // Check if this specific reference's PDF is being shown inline
    const isThisPdfInline =
      ref.document_url && ref.document_url === inlinePdfUrl

    const metricLabel = (ref as any).metric_label as string | undefined

    return (
      <ReferenceCard>
        {metricLabel && <ReferenceCardTitle>{metricLabel}</ReferenceCardTitle>}
        <ReferenceList>
          {ref.document_title && (
            <ReferenceItem>
              <strong>{t('metricSourceModal.reference.documentTitle')}:</strong>
              <span>{ref.document_title}</span>
            </ReferenceItem>
          )}

          {(ref.page_start || ref.page_end) && (
            <ReferenceItem>
              <strong>{t('metricSourceModal.reference.pages')}:</strong>
              <span>
                {ref.page_start}
                {ref.page_end && ref.page_end !== ref.page_start
                  ? ` - ${ref.page_end}`
                  : ''}
              </span>
            </ReferenceItem>
          )}
          {ref.article && (
            <ReferenceItem>
              <strong>{t('metricSourceModal.reference.article')}:</strong>
              <span>{ref.article}</span>
            </ReferenceItem>
          )}
          {ref.article_path && (
            <ReferenceItem>
              <strong>{t('metricSourceModal.reference.articlePath')}:</strong>
              <span>{ref.article_path}</span>
            </ReferenceItem>
          )}
          {/* Only show "View PDF" link if this reference's PDF is NOT being shown inline */}
          {ref.document_url && !isThisPdfInline && (
            <ReferenceItem>
              <strong>{t('metricSourceModal.reference.document')}:</strong>
              <DocumentLink
                href={ref.document_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('metricSourceModal.reference.viewPdf')}{' '}
                <Icon name="external-link" size="small" />
              </DocumentLink>
            </ReferenceItem>
          )}
        </ReferenceList>
      </ReferenceCard>
    )
  }

  const renderReferences = (refs?: ZoningReference[], title?: string) => {
    if (!refs || refs.length === 0) return null

    return (
      <Section>
        {title && <SectionTitle>{title}</SectionTitle>}
        {refs.map((ref, i) => (
          <React.Fragment key={ref.document_id || i}>
            {renderSingleReference(ref)}
          </React.Fragment>
        ))}
      </Section>
    )
  }

  /* ── Tab content ──────────────────────────────────────────────── */
  const renderTabContent = () => {
    if (isNoData) {
      return (
        <Section>
          <div style={{ color: '#999' }}>
            {t('metricSourceModal.noData.message')}
          </div>
        </Section>
      )
    }

    if (!currentTab) return null

    switch (currentTab) {
      case 'llm': {
        const llmSource = effectiveSources?.llm
        return (
          <>
            {renderReferences(
              llmSource?.reference,
              t('metricSourceModal.sections.referenceInfo')
            )}
            {llmSource?.additional_info && (
              <Section>
                <SectionTitle>
                  {t('metricSourceModal.sections.additionalInfo')}
                </SectionTitle>
                <AdditionalInfo>
                  &quot;{llmSource.additional_info}&quot;
                </AdditionalInfo>
              </Section>
            )}
            <WarningBox $type="reg">
              <WarningContent>
                <Icon name="alert-circle" size="medium" />
                <div>{t('metricSourceModal.warnings.regulationShort')}</div>
              </WarningContent>
              {canReport && (
                <ReportLink
                  onClick={() => {
                    setReportBound(selectedBound)
                    setReportMode(true)
                  }}
                >
                  {'\u2691'} {t('metricSourceModal.report.link')}
                </ReportLink>
              )}
            </WarningBox>
          </>
        )
      }

      case 'geoportal': {
        const geoportalSource = effectiveSources?.geoportal
        return (
          <>
            {localizedGeoPortalLink && (
              <Section>
                <GeoportalButton
                  href={localizedGeoPortalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon name="external-link" size="small" />
                  {t('metricSourceModal.geoportal.viewLink')}
                </GeoportalButton>
              </Section>
            )}
            {geoportalSource?.additional_info && (
              <Section>
                <SectionTitle>
                  {t('metricSourceModal.sections.additionalInfo')}
                </SectionTitle>
                <AdditionalInfo>
                  &quot;{geoportalSource.additional_info}&quot;
                </AdditionalInfo>
              </Section>
            )}
            <WarningBox $type="geo">
              <WarningContent>
                <Icon name="alert-circle" size="medium" />
                <div>{t('metricSourceModal.warnings.geoportalShort')}</div>
              </WarningContent>
              {canReport && (
                <ReportLink
                  onClick={() => {
                    setReportBound(selectedBound)
                    setReportMode(true)
                  }}
                >
                  {'\u2691'} {t('metricSourceModal.report.link')}
                </ReportLink>
              )}
            </WarningBox>
          </>
        )
      }

      case 'estimate': {
        const estimateSource = effectiveSources?.estimate
        return (
          <>
            {estimateSource?.additional_info && (
              <Section>
                <SectionTitle>
                  {t('metricSourceModal.sections.additionalInfo')}
                </SectionTitle>
                <AdditionalInfo>
                  &quot;{estimateSource.additional_info}&quot;
                </AdditionalInfo>
              </Section>
            )}
            <WarningBox $type="alg">
              <WarningContent>
                <Icon name="alert-circle" size="medium" />
                <div>{t('metricSourceModal.warnings.estimateShort')}</div>
              </WarningContent>
              {canReport && (
                <ReportLink
                  onClick={() => {
                    setReportBound(selectedBound)
                    setReportMode(true)
                  }}
                >
                  {'\u2691'} {t('metricSourceModal.report.link')}
                </ReportLink>
              )}
            </WarningBox>
          </>
        )
      }

      case 'manual': {
        const originalSrc = getOriginalSource(effectiveSources)
        return (
          <Section>
            <OverrideDescription>
              {originalSrc && isAuthoritative(originalSrc)
                ? t('metricSourceModal.userOverride.description', {
                    source: getBadgeLabel(originalSrc),
                  })
                : t('metricSourceModal.userOverride.noAuthoritativeSource')}
            </OverrideDescription>
          </Section>
        )
      }

      default:
        return null
    }
  }

  /* ── Envelope calculation breakdown (Regelbauweise) ───────────── */
  const renderEnvelopeCalculation = () => {
    if (!envelopeCalculation) return null
    return (
      <Section>
        {envelopeCalculation.title && (
          <SectionTitle>{envelopeCalculation.title}</SectionTitle>
        )}
        <CalcList>
          {envelopeCalculation.rows.map((row, index) => (
            <CalcRow key={`${row.label}-${index}`} $tone={row.tone}>
              <CalcOp>{row.op || ''}</CalcOp>
              <CalcLabelWrap>
                <CalcLabel $tone={row.tone}>{row.label}</CalcLabel>
                {row.sub && <CalcSub>{row.sub}</CalcSub>}
              </CalcLabelWrap>
              <CalcValue $tone={row.tone}>{row.value}</CalcValue>
              {row.badge === '3d' ? (
                <ThreeDChip>3D</ThreeDChip>
              ) : row.badge ? (
                <MetricSourceBadge source={row.badge} />
              ) : null}
            </CalcRow>
          ))}
        </CalcList>
        {envelopeCalculation.disclaimer && (
          <WarningBox $type="reg" style={{ marginTop: 16 }}>
            <WarningContent>
              <Icon name="info" size="medium" />
              <div>{envelopeCalculation.disclaimer}</div>
            </WarningContent>
          </WarningBox>
        )}
      </Section>
    )
  }

  /* ── Report form render ───────────────────────────────────────── */
  const renderReportForm = () => {
    if (reportSuccess) {
      return (
        <ReportSuccessMessage>
          <Icon name="check-circle" size="medium" />
          {t('metricSourceModal.report.success')}
        </ReportSuccessMessage>
      )
    }

    return (
      <ReportFormContainer>
        <SectionTitle>
          {t('metricSourceModal.report.contextTitle')}
        </SectionTitle>
        <ReportContextBlock>
          {regionName && (
            <ReportContextRow>
              <ReportContextLabel>Canton</ReportContextLabel>
              <ReportContextValue>{regionName}</ReportContextValue>
            </ReportContextRow>
          )}
          {municipalityName && (
            <ReportContextRow>
              <ReportContextLabel>Municipality</ReportContextLabel>
              <ReportContextValue>{municipalityName}</ReportContextValue>
            </ReportContextRow>
          )}
          {zoneName && (
            <ReportContextRow>
              <ReportContextLabel>Zone</ReportContextLabel>
              <ReportContextValue>{zoneName}</ReportContextValue>
            </ReportContextRow>
          )}
          <ReportContextRow>
            <ReportContextLabel>{metricLabel}</ReportContextLabel>
            <ReportContextValue>
              {isRangeReport ? `${minValue} / ${maxValue}` : value}
              {unit ? ` ${unit}` : ''}
              {currentTab && (
                <>
                  {' '}
                  <MetricSourceBadge source={currentTab} />
                </>
              )}
            </ReportContextValue>
          </ReportContextRow>
        </ReportContextBlock>

        {isRangeReport && (
          <ReportFieldLabel>
            {t('metricSourceModal.report.whichValue', {
              defaultValue: 'Which value is incorrect?',
            })}
            <ReportBoundSelector>
              <ReportBoundOption
                $selected={reportBound === 'min'}
                onClick={() => setReportBound('min')}
              >
                MIN: {minValue}
                {unit ? ` ${unit}` : ''}
              </ReportBoundOption>
              <ReportBoundOption
                $selected={reportBound === 'max'}
                onClick={() => setReportBound('max')}
              >
                MAX: {maxValue}
                {unit ? ` ${unit}` : ''}
              </ReportBoundOption>
            </ReportBoundSelector>
          </ReportFieldLabel>
        )}

        <ReportFieldLabel>
          {t('metricSourceModal.report.suggestedValue')}
          <ReportInput
            type="number"
            step="any"
            value={reportSuggestedValue}
            onChange={(e) => setReportSuggestedValue(e.target.value)}
            placeholder={t(
              'metricSourceModal.report.suggestedValuePlaceholder'
            )}
          />
        </ReportFieldLabel>

        <ReportFieldLabel>
          {t('metricSourceModal.report.explanation')}
          <ReportTextarea
            value={reportExplanation}
            onChange={(e) => setReportExplanation(e.target.value)}
            placeholder={t('metricSourceModal.report.explanationPlaceholder')}
          />
        </ReportFieldLabel>

        {reportError && (
          <WarningBox $type="conflict">
            <WarningContent>
              <Icon name="alert-circle" size="small" />
              <div>{t('metricSourceModal.report.error')}</div>
            </WarningContent>
          </WarningBox>
        )}

        <ReportButtonGroup>
          <ReportCancelButton onClick={handleReportCancel}>
            {t('metricSourceModal.report.cancel')}
          </ReportCancelButton>
          <ReportSubmitButton
            onClick={handleReportSubmit}
            disabled={!reportExplanation.trim() || reportSubmitting}
            $loading={reportSubmitting}
          >
            {reportSubmitting ? '...' : t('metricSourceModal.report.submit')}
          </ReportSubmitButton>
        </ReportButtonGroup>
      </ReportFormContainer>
    )
  }

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent $hasPDF={hasRightPanel}>
        <ModalHeader $hasPDF={hasRightPanel}>
          {(municipalityName || planName || zoneName) && (
            <>
              <HeaderContext>
                {municipalityName && <span>{municipalityName}</span>}
                {municipalityName && (planName || zoneName) && (
                  <ContextChevron />
                )}
                {planName && <span>{planName}</span>}
                {planName && zoneName && <ContextChevron />}
                {zoneName && <span>{zoneName}</span>}
              </HeaderContext>
              <HeaderDivider />
            </>
          )}
          <HeaderTopRow>
            <ModalTitle>
              {metricLabel} {t('metricSourceModal.titleDetails')}
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <Icon name="x" size="medium" />
            </CloseButton>
          </HeaderTopRow>
        </ModalHeader>

        <DetailsPanel $hasPDF={hasRightPanel}>
          {renderHero()}
          {renderEnvelopeCalculation()}
          {renderBoundSelector()}
          {renderStatusBar()}
          {renderTabs()}
          {reportMode ? renderReportForm() : renderTabContent()}
        </DetailsPanel>

        {/* Right Panel — PDF for REG, map for GEO, estimate stats for ALG */}
        {hasRightPanel && rightPanelContent === 'pdf' && pdfUrl && (
          <PDFViewerPanel>
            <PDFViewer
              key={`pdf-${currentTab}`}
              url={pdfUrl}
              initialPage={pdfInitialPage}
              showThumbnails={true}
              enableSearch={true}
              enableDownload={true}
              enableFullscreen={true}
              height="100%"
            />
          </PDFViewerPanel>
        )}

        {hasRightPanel && rightPanelContent === 'map' && geoCenter && (
          <GeoportalPanel>
            <ZoningMapViewer
              key={JSON.stringify(geoCenter)}
              geoCenter={geoCenter}
              regionName={regionName}
              landGeoPolygon={landGeoPolygon}
            />
          </GeoportalPanel>
        )}

        {hasRightPanel && rightPanelContent === 'estimate' && (
          <EstimatePanel
            stats={estimateStats}
            loading={estimateStatsLoading}
            geoCenter={
              typeof geoCenter === 'object' &&
              geoCenter !== null &&
              'lat' in geoCenter
                ? (geoCenter as { lat: number; lon: number })
                : undefined
            }
            geoPolygon={geoPolygon}
            landGeoPolygon={landGeoPolygon}
            metricLabel={metricLabel}
            value={
              typeof effectiveValue === 'string'
                ? Number(effectiveValue)
                : effectiveValue
            }
            unit={unit}
            metricKey={metricKey}
          />
        )}

        {hasRightPanel &&
          rightPanelContent === 'manual' &&
          (() => {
            const originalSrc = getOriginalSource(effectiveSources)
            const originalValue = originalSrc
              ? getSourceValue(effectiveSources, originalSrc)
              : undefined
            const manualValue = value
            const sourceBadge = originalSrc
              ? getBadgeLabel(originalSrc)
              : undefined
            const diff =
              originalValue != null && manualValue != null
                ? Number(manualValue) - Number(originalValue)
                : null
            const diffStr =
              diff != null ? `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}` : null

            const step1TitleKey =
              originalSrc === 'llm'
                ? 'metricSourceModal.userOverride.timeline.step1TitleReg'
                : originalSrc === 'geoportal'
                  ? 'metricSourceModal.userOverride.timeline.step1TitleGeo'
                  : 'metricSourceModal.userOverride.timeline.step1Title'

            return (
              <ManualTimelinePanel>
                <TimelineTitle>
                  {t('metricSourceModal.userOverride.timeline.title')}
                </TimelineTitle>

                {/* Step 1: Original source */}
                <TimelineStep>
                  <TimelineStepIndicator>
                    <TimelineStepCircle $variant="default">
                      1
                    </TimelineStepCircle>
                    <TimelineConnector />
                  </TimelineStepIndicator>
                  <TimelineStepContent>
                    <TimelineStepTitle>{t(step1TitleKey)}</TimelineStepTitle>
                    <TimelineStepDescription>
                      {originalSrc && originalValue != null
                        ? t(
                            'metricSourceModal.userOverride.timeline.step1Desc',
                            {
                              source: sourceBadge,
                              value: originalValue,
                            }
                          )
                        : t(
                            'metricSourceModal.userOverride.timeline.step1NoSource'
                          )}
                    </TimelineStepDescription>
                  </TimelineStepContent>
                </TimelineStep>

                {/* Step 2: User override */}
                <TimelineStep>
                  <TimelineStepIndicator>
                    <TimelineStepCircle $variant="warning">
                      2
                    </TimelineStepCircle>
                    <TimelineConnector />
                  </TimelineStepIndicator>
                  <TimelineStepContent>
                    <TimelineHighlightBox>
                      <TimelineHighlightTitle>
                        {t(
                          'metricSourceModal.userOverride.timeline.step2Title',
                          {
                            value: manualValue,
                          }
                        )}
                      </TimelineHighlightTitle>
                      <TimelineHighlightDescription>
                        {originalSrc && originalValue != null
                          ? t(
                              'metricSourceModal.userOverride.timeline.step2Desc',
                              {
                                source: sourceBadge,
                                originalValue,
                              }
                            )
                          : t(
                              'metricSourceModal.userOverride.timeline.step2DescNoSource'
                            )}
                      </TimelineHighlightDescription>
                    </TimelineHighlightBox>
                  </TimelineStepContent>
                </TimelineStep>

                {/* Step 3: Result */}
                <TimelineStep>
                  <TimelineStepIndicator>
                    <TimelineStepCircle $variant="success">
                      3
                    </TimelineStepCircle>
                  </TimelineStepIndicator>
                  <TimelineStepContent>
                    <TimelineStepTitle>
                      {t('metricSourceModal.userOverride.timeline.step3Title', {
                        value: manualValue,
                      })}
                    </TimelineStepTitle>
                    <TimelineStepDescription>
                      {diffStr != null
                        ? t(
                            'metricSourceModal.userOverride.timeline.step3Desc',
                            {
                              diff: diffStr,
                            }
                          )
                        : t(
                            'metricSourceModal.userOverride.timeline.step3DescNoOriginal'
                          )}
                    </TimelineStepDescription>
                  </TimelineStepContent>
                </TimelineStep>
              </ManualTimelinePanel>
            )
          })()}

        {hasRightPanel && rightPanelContent === 'empty' && (
          <EmptyRightPanel>
            <EmptyIcon>📄</EmptyIcon>
            <EmptyText>{t('metricSourceModal.noDocument.message')}</EmptyText>
          </EmptyRightPanel>
        )}
      </ModalContent>
    </ModalOverlay>
  )
}
